import { deadlineDays, writeRange } from './periodSteps';
import { Tip } from './style';

// Frosted glass: translucent and blurred, so the card sits on the page, not in it.
const GLASS = 'rgba(23, 26, 34, 0.82)';
const FROST = 'blur(18px) saturate(150%)';

/** @type {Partial<import('@mantine/core').TooltipProps>} */
export const DEADLINE_TIP_PROPS = {
  radius: 'md',
  multiline: true,
  w: 300,
  withArrow: true,
  // Centred under its target, arrow in the middle — not in a rounded corner.
  position: 'bottom',
  arrowPosition: 'center',
  arrowSize: 7,
  styles: {
    tooltip: {
      padding: '10px 12px 12px',
      background: GLASS,
      backdropFilter: FROST,
      WebkitBackdropFilter: FROST,
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.28)'
    },
    arrow: { background: GLASS, backdropFilter: FROST, WebkitBackdropFilter: FROST }
  }
};

const dayOf = at => new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
const weekday = at => at.toLocaleDateString('en-GB', { weekday: 'short' });

// A day's dot: passed, the next one due (amber inside a week), or further ahead.
const stateOf = (day, next, now) => {
  if (dayOf(day.at) < dayOf(now)) return 'past';
  if (day !== next) return 'ahead';
  return (dayOf(day.at) - dayOf(now)) / 86400000 <= 7 ? 'soon' : 'next';
};

const Timeline = ({ days, now, current, compact }) => {
  const next = days.find(day => dayOf(day.at) >= dayOf(now));
  return (
    // A year in the date widens the column.
    <ol
      className="line"
      data-compact={compact}
      data-long={days.some(day => /\d{4}/.test(day.date))}
    >
      {days.map(day => (
        <li key={day.date} data-state={stateOf(day, next, now)}>
          <i className="dot" aria-hidden />
          <span className="when">
            <em>{weekday(day.at)}</em> {day.date}
          </span>
          <span className="who">
            {day.items.map(item => (
              <span
                key={`${item.code}-${item.name}`}
                className="country"
                data-current={!!current && item.code === current}
              >
                {item.code && <code>{item.code}</code>}
                {!compact && item.name}
              </span>
            ))}
          </span>
        </li>
      ))}
    </ol>
  );
};

// The filing dates as a timeline; the weekday says why countries land on different days.
export const DeadlineTip = ({ due, id, period, headline, status, current }) => {
  const now = new Date();
  const { days, fundOfFunds } = deadlineDays(due.entries);
  const code = current ? String(current).toUpperCase() : null;
  // Past four countries, codes alone: a column of names is unreadable.
  const compact = days.reduce((count, day) => count + day.items.length, 0) > 4;
  return (
    <Tip id={id}>
      <div className="eyebrow">{period ? `Filing deadlines · ${period}` : 'Filing deadlines'}</div>
      {headline && (
        <div className="head">
          <b>{headline}</b>
          {status && <span className="status">{status}</span>}
        </div>
      )}
      {days.length > 0 ? (
        <Timeline days={days} now={now} current={code} compact={compact} />
      ) : (
        <div className="note first">
          {due.isProjected
            ? 'Projected: a month after the period closes. Country rules apply once it exists.'
            : 'No filing dates for this period yet.'}
        </div>
      )}
      {/* One rule, fifteen days on each date — a line, not a second list. */}
      {fundOfFunds.length > 0 && (
        <div className="fof">
          <span className="eyebrow">Funds of funds</span>
          <b>{writeRange(fundOfFunds[0].at, fundOfFunds[fundOfFunds.length - 1].at, now)}</b>
        </div>
      )}
      {due.isPredicted && (
        <div className="note">Predicted from the last period with adviser assets</div>
      )}
    </Tip>
  );
};
